import { ConstantsCpbxApi } from "../constants/ConstantsCpbxApi";
import { LocalCacheHelper } from "./LocalCacheHelper";
import { ConstantsHttpHeader } from "../constants/ConstantsHttpHeader";

export class CpbxApiHelper {

    public static GetCpbxRequestHeadersWithoutAuth(): Headers {
        const cpbxHeaders: Headers = new Headers();
        cpbxHeaders.append("Content-Type", "application/json; charset=UTF-8");
        cpbxHeaders.append("X-BroadWorks-Protocol-Version", "2.0");
        return cpbxHeaders;
    }

    // remove this after psa-change
    public static GetCpbxRequestHeadersWithAuth(basicAuthStr: string): Headers {
        const cpbxHeaders: Headers = this.GetCpbxRequestHeadersWithoutAuth();
        cpbxHeaders.append("Authorization", "Basic " + basicAuthStr);
        return cpbxHeaders;
    }

    public static GetCpbxRequestHeadersWithAuthPsa(basicAuthStrEnrypted: string, m365UserObjectId: string, userIV: string): Headers {
        const cpbxHeaders: Headers = this.GetCpbxRequestHeadersWithoutAuth();
        cpbxHeaders.append("Authorization", "Basic " + basicAuthStrEnrypted);
        cpbxHeaders.append(ConstantsHttpHeader.XTeamsAppUserId, m365UserObjectId);
        cpbxHeaders.append(ConstantsHttpHeader.XTeamsAppUserIV, userIV);
        cpbxHeaders.append(ConstantsHttpHeader.XTeamsAppCpbx1GroupId, LocalCacheHelper.GetLocalStorageItemCbpxUserGroupIdFromProfileMe());
        return cpbxHeaders;
    }

    public static GetProxyUrlForEncryptAuthorizationHeader(): string {
        // e.g.: https://wa-cpbx4-myexpressapp.azurewebsites.net/psa/EncryptAuthorizationHeader
        return (
            LocalCacheHelper.GetLocalStorageItemCbpxProxyUrl() +
            ConstantsCpbxApi.EncryptAuthorizationHeaderUrl
        );
    }

    private static GetCpbxUrlForUser(): string {
        return (
            LocalCacheHelper.GetLocalStorageItemCbpxProxyUrl() +
            ConstantsCpbxApi.ProxyUrl +
            ConstantsCpbxApi.PartApiPath +
            ConstantsCpbxApi.Part1User
        );
    }

    public static GetCpbxUrlForUserProfileWithName(cpbxUserName: string): string {
        // in case user never logged in before we have to use username from input-value (param-value)
        // e.g.: https://wa-cpbx4-myexpressapp.azurewebsites.net/cpbxapi/com.broadsoft.xsi-actions/v2.0/user/axels@deutschland-lan.de/profile?format=json
        return (
            this.GetCpbxUrlForUser() +
            "/" +
            cpbxUserName +
            ConstantsCpbxApi.Part3Profile +
            ConstantsCpbxApi.QueryFormatAsJson
        );
    }

    public static GetCpbxUrlForServicesCallingLineIDDeliveryBlocking(cpbxUserName: string): string {
        // e.g.: https://wa-cpbx4-myexpressapp.azurewebsites.net/cpbxapi/com.broadsoft.xsi-actions/v2.0/user/axels@deutschland-lan.de/services/CallingLineIDDeliveryBlocking?format=json
        return (
            this.GetCpbxUrlForUser() +
            "/" +
            cpbxUserName +
            ConstantsCpbxApi.Part3Services +
            ConstantsCpbxApi.Part4CallingLineIDDeliveryBlocking +
            ConstantsCpbxApi.QueryFormatAsJson
        );
    }

    public static GetCpbxUrlForServicesDoNotDisturb(cpbxUserName: string): string {
        // e.g.: https://wa-cpbx4-myexpressapp.azurewebsites.net/cpbxapi/com.broadsoft.xsi-actions/v2.0/user/axels@deutschland-lan.de/services/DoNotDisturb?format=json
        return (
            this.GetCpbxUrlForUser() +
            "/" +
            cpbxUserName +
            ConstantsCpbxApi.Part3Services +
            ConstantsCpbxApi.Part4DoNotDisturb +
            ConstantsCpbxApi.QueryFormatAsJson
        );
    }

    public static GetCpbxUrlCalls(cpbxUserName: string): string {
        // e.g.: https://wa-cpbx4-myexpressapp.azurewebsites.net/cpbxapi/com.broadsoft.xsi-actions/v2.0/user/{{user_id}}/calls?format=json

        return (
            this.GetCpbxUrlForUser() +
            "/" +
            cpbxUserName +
            ConstantsCpbxApi.Part3Calls +
            ConstantsCpbxApi.QueryFormatAsJson
        );
    }

    public static GetCpbxUrlCallsCallRetrieve(cpbxUserName: string): string {
        // e.g.: https://wa-cpbx4-myexpressapp.azurewebsites.net/cpbxapi/com.broadsoft.xsi-actions/v2.0/user/{{user_id}}/calls/CallRetrieve?location=All&format=json

        return (
            this.GetCpbxUrlForUser() +
            "/" +
            cpbxUserName +
            ConstantsCpbxApi.Part3Calls +
            ConstantsCpbxApi.Part4CallRetrieve +
            ConstantsCpbxApi.QueryFormatAsJson
        );
    }

    public static GetCpbxUrlCallsLastNumberRedial(cpbxUserName: string): string {
        // e.g.: https://wa-cpbx4-myexpressapp.azurewebsites.net/cpbxapi/com.broadsoft.xsi-actions/v2.0/user/{{user_id}}/calls/LastNumberRedial?location=All&format=json

        return (
            this.GetCpbxUrlForUser() +
            "/" +
            cpbxUserName +
            ConstantsCpbxApi.Part3Calls +
            ConstantsCpbxApi.Part4LastNumberRedial +
            ConstantsCpbxApi.QueryFormatAsJson
        );
    }

    public static GetCpbxUrlCallsCallPickup(cpbxUserName: string): string {
        // e.g.: https://wa-cpbx4-myexpressapp.azurewebsites.net/cpbxapi/com.broadsoft.xsi-actions/v2.0/user/{{user_id}}/calls/CallPickup?location=All&format=json

        return (
            this.GetCpbxUrlForUser() +
            "/" +
            cpbxUserName +
            ConstantsCpbxApi.Part3Calls +
            ConstantsCpbxApi.Part4CallPickup +
            ConstantsCpbxApi.QueryFormatAsJson +
            "&location=All"
        );
    }

    public static GetCpbxUrlForDirectoriesEnterpriseDefault(cpbxUserName: string): string {
        return this.GetCpbxUrlForDirectoriesEnterpriseSubset(cpbxUserName, 1, 10, "lastName", "");
    }

    public static GetCpbxUrlForDirectoriesEnterpriseSubset(
        cpbxUserName: string, startAt: number, noOfResults: number, sortColumn: string, filterValue: string): string {
        // e.g.: https://wa-cpbx4-myexpressapp.azurewebsites.net/cpbxapi/com.broadsoft.xsi-actions/v2.0/user/{{user_id}}/directories/Enterprise/?format=json&results=50&start=50

        // hardening
        if (startAt < 1) {
            throw new Error("Input param startAt ['" + startAt + "'] has to be bigger than 0!");
        }
        if (noOfResults < 1) {
            throw new Error("Input param noOfResults ['" + noOfResults + "'] has to be bigger than 0!");
        }
        if (noOfResults > 1000) {
            throw new Error("Input param noOfResults ['" + noOfResults + "'] has to be smaller or equals to 1000!");
        }

        // hardening sort-column - there are just 3 columns which are supported by the interface
        switch (sortColumn.toLocaleLowerCase()) {
            case "firstname": {
                sortColumn = "firstName";
                break;
            }
            case "lastname": {
                sortColumn = "lastName";
                break;
            }
            case "department": {
                sortColumn = "department";
                break;
            }
            default: {
                sortColumn = "lastName";
            }
        }

        let searchQueryFull: string = "";
        if (filterValue !== undefined && filterValue !== null && filterValue.length > 0) {
            // pay attention: max length of complete url has to be smaller than 360
            // the more search-params we add the longer the complete url will be
            /*eslint-disable */
            searchQueryFull = (
                "&searchCriteriaModeOr=true" + // search criteria should be combined as OR (not AND)
                "&firstName=*" + filterValue + "*/i" +      // Axel
                "&lastName=*" + filterValue + "*/i" +       // Schneider
                // "&name=*" + filterValue + "*/i" +        // could be firstName, lastName, Hiragana Last Name or Hiragana First Name
                // "&userId=*" + filterValue + "*/i" +      // axels@deutschland-lan.de
                // "&groupId=*" + filterValue + "*/i" +     // 551126975220gg3141
                // "&number=*" + filterValue + "*/i" +      // 49228410126240
                "&extension=*" + filterValue + "*/i" +      // 240
                "&mobileNo=*" + filterValue + "*/i"         // 491605393937
                // "&department=*" + filterValue + "*/i" +  // d424
                // "&emailAddress=*" + filterValue + "*/i"  // axel.schneider@t-systems.com
            );
            /* eslint-enable */
        }

        return (
            this.GetCpbxUrlForUser() +
            "/" +
            cpbxUserName +
            ConstantsCpbxApi.Part3Directories +
            ConstantsCpbxApi.Part4Enterprise +
            ConstantsCpbxApi.QueryFormatAsJson +
            "&start=" + startAt +
            "&results=" + noOfResults +
            "&sortColumn=" + sortColumn +
            searchQueryFull
        );
    }

    public static GetCpbxUrlForSearchDirectoryForUserExtensionNo(cpbxUserName: string, extensionValue: string): string {
        // e.g.: https://wa-cpbx4-myexpressapp.azurewebsites.net/cpbxapi/com.broadsoft.xsi-actions/v2.0/user/{{user_id}}/directories/Enterprise/?format=json&results=1&start=1

        return (
            this.GetCpbxUrlForUser() +
            "/" +
            cpbxUserName +
            ConstantsCpbxApi.Part3Directories +
            ConstantsCpbxApi.Part4Enterprise +
            ConstantsCpbxApi.QueryFormatAsJson +
            "&start=1" +
            "&results=1" +
            "&extension=" + extensionValue
        );
    }

    public static GetCpbxUrlForSearchDirectoryForUserNumber(cpbxUserName: string, numberValue: string): string {
        // e.g.: https://wa-cpbx4-myexpressapp.azurewebsites.net/cpbxapi/com.broadsoft.xsi-actions/v2.0/user/{{user_id}}/directories/Enterprise/?format=json&results=1&start=1&number=+49228410126910

        return (
            this.GetCpbxUrlForUser() +
            "/" +
            cpbxUserName +
            ConstantsCpbxApi.Part3Directories +
            ConstantsCpbxApi.Part4Enterprise +
            ConstantsCpbxApi.QueryFormatAsJson +
            "&start=1" +
            "&results=1" +
            "&number=" + encodeURIComponent(numberValue.replace(" ", ""))
        );
    }

    public static GetCpbxUrlForSearchDirectoryForExtension(cpbxUserName: string, extensionNumber: string): string {
        // e.g.: https://wa-cpbx4-myexpressapp.azurewebsites.net/cpbxapi/com.broadsoft.xsi-actions/v2.0/user/{{user_id}}/directories/Enterprise/?format=json&results=1&start=1&number=+49228410126910

        return (
            this.GetCpbxUrlForUser() +
            "/" +
            cpbxUserName +
            ConstantsCpbxApi.Part3Directories +
            ConstantsCpbxApi.Part4Enterprise +
            ConstantsCpbxApi.QueryFormatAsJson +
            "&extension=" +
            encodeURIComponent(extensionNumber)
        );
    }

    public static GetCpbxUrlCallsDirectedCallPickupForUserId(cpbxUserName: string, cpbxUserIdForUserToPickupCall: string): string {
        // e.g.: https://wa-cpbx4-myexpressapp.azurewebsites.net/cpbxapi/com.broadsoft.xsi-actions/v2.0/user/{{user_id}}/calls/DirectedCallPickup?address=andreas01.test@deutschland-lan.de

        return (
            this.GetCpbxUrlForUser() +
            "/" +
            cpbxUserName +
            ConstantsCpbxApi.Part3Calls +
            ConstantsCpbxApi.Part4DirectedCallPickup +
            ConstantsCpbxApi.QueryFormatAsJson +
            "&address=" + cpbxUserIdForUserToPickupCall
        );
    }

    public static GetCpbxUrlForServicesIntegratedImp(cpbxUserName: string): string {
        // e.g.: https://wa-cpbx4-myexpressapp.azurewebsites.net/cpbxapi/com.broadsoft.xsi-actions/v2.0/user/{{user_id}}/services/IntegratedIMP

        return (
            this.GetCpbxUrlForUser() +
            "/" +
            cpbxUserName +
            ConstantsCpbxApi.Part3Services +
            ConstantsCpbxApi.Part4IntegratedIMP +
            ConstantsCpbxApi.QueryFormatAsJson
        );
    }
}
